'use client';

import { useState } from 'react';
import { Calendar, Check, Clock, ExternalLink, MapPin, X } from 'lucide-react';
import type { FinderGroup, MyGroup, MyInquiry } from '@/lib/types';
import { campusShort } from '@/lib/format';
import { cancelInquiry } from '@/lib/api';
import { SEE_JESUS_COPY } from '@/lib/seeJesus';
import { Avatar } from './Avatar';
import { GroupTags } from './GroupCard';
import { InquiryForm } from './InquiryForm';
import { RichDescription } from './RichDescription';
import { MineBadge, StatusBadge } from './chips';

/**
 * Everything below a group's header: when and where, leaders, roster for
 * members, tags, the description, and the inquiry form (or the Group
 * Connect hand-off for members). The details page and the finder's sheet
 * both render this, so a group reads the same in both places.
 */
export interface GroupDetailsBodyProps {
  apiBaseUrl: string;
  group: FinderGroup;
  mine?: MyGroup;
  /** Recent inquiries into this group by the visitor's household */
  inquiries?: MyInquiry[];
  /** An inquiry just went out: the caller refreshes `inquiries` */
  onInquired?: () => void;
  /** Opens the "help me find a group" form (else the link) */
  onHelp?: () => void;
  /** The caller takes over cancelling (the finder closes the sheet and shows it on the card) */
  onCancel?: (inquiry: MyInquiry) => void;
  finderUrl: string;
  inquiryFormUrl: string;
  onExplainSeeJesus: () => void;
  /** Tighter spacing for the sheet */
  compact?: boolean;
}

const ICON_WRAP = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full';

function inquiryLine(i: MyInquiry): string {
  const when = new Date(i.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (i.status === 'placed') return 'Placed in this group.';
  if (i.status === 'closed') return `Inquired ${when}. That inquiry has been closed.`;
  return `Inquired ${when}. The leaders have it and will reach out.`;
}

export function Heading({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <h3
      className={`mb-3 text-xs font-bold tracking-widest uppercase ${dark ? 'text-[#c79854]' : 'text-gray-500'}`}
    >
      {children}
    </h3>
  );
}

export function GroupDetailsBody({
  apiBaseUrl,
  group,
  mine,
  inquiries = [],
  onInquired,
  onHelp,
  onCancel,
  finderUrl,
  inquiryFormUrl,
  onExplainSeeJesus,
  compact = false,
}: GroupDetailsBodyProps) {
  // Withdrawing a waiting inquiry: a two-step "Cancel" then "Yes, cancel",
  // no dialog. The caller refreshes the list once the row is gone.
  const [confirming, setConfirming] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState<number | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const withdraw = async (inquiryId: number) => {
    setCancelling(inquiryId);
    setCancelError(null);
    try {
      await cancelInquiry(apiBaseUrl, group.id, inquiryId);
      setConfirming(null);
      onInquired?.();
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Could not cancel the inquiry');
    } finally {
      setCancelling(null);
    }
  };
  const sj = group.seeJesus;
  const iconWrap = `${ICON_WRAP} ${sj ? 'bg-white/10 text-[#f6efe9]/70' : 'bg-gray-100 text-gray-500'}`;
  const address = mine?.address;
  const addressLines = address
    ? [
        address.line1,
        address.line2,
        [address.city, address.state].filter(Boolean).join(', ') +
          (address.zip ? ` ${address.zip}` : ''),
      ].filter((l) => l && l.trim())
    : [];
  const campusFinderUrl = group.campusId
    ? `${finderUrl}?campus=${campusShort(group.campusName).toLowerCase().replace(/\s+/g, '-')}`
    : finderUrl;

  return (
    <div className="@container">
      {/*
        Layout follows the body's own width, not the viewport, because the
        same body renders in the finder's sheet and on the details page.
        The facts sit in columns across the top as room allows (two from
        ~450px, three from ~900px with the description taking the widest
        track), the detail tags run in one row beneath them, and the
        inquiry panel is a full-width row of its own below that.
      */}
      <div
        className={`flex flex-col gap-8 p-6 sm:p-8 @4xl:gap-10 @4xl:p-10 ${
          sj ? 'sj-type text-[#f6efe9]' : 'text-[#1c2b39]'
        }`}
      >
        {(mine || group.full || group.paused) && (
          <div className="flex flex-wrap items-center gap-2">
            {mine && <MineBadge role={mine.role} />}
            <StatusBadge full={group.full} paused={group.paused} />
          </div>
        )}

        <div className="grid gap-8 @md:grid-cols-2 @4xl:grid-cols-[repeat(2,minmax(0,1fr))_minmax(0,1.6fr)] @4xl:gap-10">
          <section>
            <Heading dark={sj}>When and where</Heading>
            <ul className="flex flex-col gap-3 text-sm">
              {(group.occurrence || group.day) && (
                <li className="flex items-center gap-3">
                  <span className={iconWrap}>
                    <Calendar className="h-4 w-4" />
                  </span>
                  {group.occurrence || group.day}
                </li>
              )}
              {group.time && (
                <li className="flex items-center gap-3">
                  <span className={iconWrap}>
                    <Clock className="h-4 w-4" />
                  </span>
                  {group.time}
                </li>
              )}
              <li className="flex items-start gap-3">
                <span className={iconWrap}>
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="pt-1.5">
                  {addressLines.length > 0 ? (
                    <span className="flex flex-col">
                      {addressLines.map((l) => (
                        <span key={l}>{l}</span>
                      ))}
                    </span>
                  ) : (
                    <>Meets in {group.city}</>
                  )}
                </span>
              </li>
            </ul>
          </section>

          {group.leaders.length > 0 && (
            <section>
              <Heading dark={sj}>{group.leaders.length === 1 ? 'Leader' : 'Leaders'}</Heading>
              <ul className="flex flex-col gap-3">
                {group.leaders.map((l) => (
                  <li key={l.name} className="flex items-center gap-3 text-sm font-semibold">
                    <Avatar name={l.name} imageUrl={l.imageUrl} size={36} />
                    {l.name}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="@md:col-span-2 @4xl:col-span-1">
            <Heading dark={sj}>About us</Heading>
            {group.description || group.seeJesus ? (
              <RichDescription
                text={group.description || SEE_JESUS_COPY.lead}
                className={`text-[15px] ${sj ? 'text-[#f6efe9]/85' : 'text-gray-700'}`}
              />
            ) : (
              <p className={`text-sm italic ${sj ? 'text-[#f6efe9]/50' : 'text-gray-400'}`}>
                No description yet.
              </p>
            )}
          </section>

          {mine && mine.members.length > 0 && (
            <section className="@md:col-span-2 @4xl:col-span-3">
              <Heading dark={sj}>Who's in the group</Heading>
              <ul className="flex flex-wrap gap-3">
                {mine.members.map((m) => (
                  <li
                    key={m.contactId}
                    className="flex w-16 flex-col items-center gap-1 text-center"
                  >
                    <Avatar name={m.name} imageUrl={m.imageUrl} size={44} />
                    <span
                      className={`w-full truncate text-[11px] leading-tight ${sj ? 'text-[#f6efe9]/70' : 'text-gray-600'}`}
                    >
                      {m.name.split(' ')[0]}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <GroupTags group={group} dark={sj} onSeeJesus={onExplainSeeJesus} />

        <section className={`border-t pt-8 ${sj ? 'border-white/10' : 'border-gray-200'}`}>
          {mine ? (
            <div className="flex flex-col items-start gap-3">
              <p className="text-base font-bold">
                {mine.role === 'leader' ? 'You lead this group.' : "You're in this group."}
              </p>
              <p className={`text-sm ${sj ? 'text-[#f6efe9]/75' : 'text-gray-600'}`}>
                Attendance, members and messages live in Group Connect.
              </p>
              <a
                href={mine.connectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1c2b39] px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:scale-[1.02] hover:shadow-md active:scale-[0.98]"
              >
                Open in Group Connect
                <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
              </a>
            </div>
          ) : (
            <>
              {inquiries.length > 0 && (
                <ul className="mb-6 flex flex-col gap-2.5">
                  {inquiries.map((i) => (
                    <li key={i.id} className="flex items-center gap-3 text-sm">
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                          i.status === 'closed'
                            ? sj
                              ? 'bg-white/10 text-[#f6efe9]/60'
                              : 'bg-gray-200 text-gray-500'
                            : sj
                              ? 'sj-foil text-black'
                              : 'bg-[#299c8f] text-white'
                        }`}
                      >
                        {i.status === 'closed' ? (
                          <X className="h-4 w-4" strokeWidth={3} />
                        ) : (
                          <Check className="h-4 w-4" strokeWidth={3} />
                        )}
                      </span>
                      <span className={sj ? 'text-[#f6efe9]/85' : 'text-gray-700'}>
                        <span className={`font-bold ${sj ? 'text-[#f6efe9]' : 'text-[#1c2b39]'}`}>
                          {i.name}
                        </span>
                        {' · '}
                        {inquiryLine(i)}
                      </span>
                      {i.status === 'pending' &&
                        (confirming === i.id ? (
                          <span className="ml-auto flex shrink-0 items-center gap-1">
                            <button
                              type="button"
                              onClick={() => (onCancel ? onCancel(i) : void withdraw(i.id))}
                              disabled={cancelling === i.id}
                              className={`rounded-full px-3 py-1 text-xs font-bold transition-all hover:scale-105 active:scale-95 disabled:opacity-60 ${sj ? 'bg-white/10 text-[#f6efe9]' : 'bg-red-50 text-red-700'}`}
                            >
                              {cancelling === i.id ? 'Cancelling…' : 'Yes, cancel it'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirming(null)}
                              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${sj ? 'text-[#f6efe9]/70 hover:bg-white/10' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                              Keep
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirming(i.id)}
                            className={`ml-auto shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${sj ? 'text-[#f6efe9]/60 hover:bg-white/10 hover:text-[#f6efe9]' : 'text-gray-500 hover:bg-gray-100 hover:text-[#1c2b39]'}`}
                          >
                            Cancel
                          </button>
                        ))}
                    </li>
                  ))}
                  {cancelError && (
                    <li className="text-sm font-semibold text-red-600">{cancelError}</li>
                  )}
                </ul>
              )}
              <InquiryForm
                tone={sj ? 'dark' : 'light'}
                apiBaseUrl={apiBaseUrl}
                group={group}
                inquired={inquiries}
                onSent={onInquired}
                onHelp={onHelp}
                finderUrl={campusFinderUrl}
                inquiryFormUrl={inquiryFormUrl}
              />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
